import React, { useState } from 'react';
import { base44 } from '@/api/base44Client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { Upload, CheckCircle2, AlertCircle, FileJson, Package } from 'lucide-react';
import { toast } from 'sonner';

const CHUNK_SIZE = 40 * 1024 * 1024; // 40MB chunks

async function calculateSHA256(data) {
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

async function chunkFile(file) {
  const totalSize = file.size;
  const partCount = Math.ceil(totalSize / CHUNK_SIZE);
  const parts = [];

  for (let i = 0; i < partCount; i++) {
    const start = i * CHUNK_SIZE;
    const end = Math.min(start + CHUNK_SIZE, totalSize);
    const chunk = file.slice(start, end);
    const arrayBuffer = await chunk.arrayBuffer();
    const sha256 = await calculateSHA256(arrayBuffer);

    parts.push({
      index: i,
      fileName: `${file.name}.part${String(i).padStart(5, '0')}`,
      byteOffset: start,
      byteLength: end - start,
      sha256,
      data: new Blob([arrayBuffer])
    });
  }

  // Non calcolare SHA256 del file intero per risparmiare memoria
  return {
    manifest: {
      schema: 'file-chunk-manifest/v1',
      original: {
        fileName: file.name,
        byteLength: totalSize,
        sha256: null // Non calcolato per file grandi
      },
      chunking: {
        maxPartBytes: CHUNK_SIZE,
        partCount
      },
      parts: parts.map(({ data, ...rest }) => rest)
    },
    parts
  };
}

async function reconstructFile(manifest, partUrls) {
  // Fetch all parts
  const partDataArray = await Promise.all(
    partUrls.map(url => fetch(url).then(res => res.arrayBuffer()))
  );

  // Map parts by filename
  const partDataMap = new Map();
  manifest.parts.forEach((partInfo, index) => {
    partDataMap.set(partInfo.fileName, new Uint8Array(partDataArray[index]));
  });

  // Allocate output buffer
  const outputBuffer = new Uint8Array(manifest.original.byteLength);

  // Reconstruct
  for (const partInfo of manifest.parts) {
    const partData = partDataMap.get(partInfo.fileName);
    if (partData) {
      outputBuffer.set(partData, partInfo.byteOffset);
    }
  }

  // Create blob
  return new Blob([outputBuffer], { type: 'application/octet-stream' });
}

export default function ChunkedFileUpload({ onUploadComplete, accept = '*', label = 'Upload File', disabled = false }) {
  const [mode, setMode] = useState('single'); // 'single' or 'manual'
  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState('idle'); // idle, uploading, complete, error
  const [uploadedFile, setUploadedFile] = useState(null);
  
  // Manual mode states
  const [manifest, setManifest] = useState(null);
  const [partFiles, setPartFiles] = useState([]);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    setStatus('uploading');
    setProgress(0);

    try {
      const fileSize = file.size;
      const useChunking = fileSize > 50 * 1024 * 1024; // Use chunking for files > 50MB

      if (!useChunking) {
        // Standard upload
        setProgress(50);
        const { file_url } = await base44.integrations.Core.UploadFile({ file });
        setProgress(100);
        setStatus('complete');
        setUploadedFile({ url: file_url, fileName: file.name, chunked: false });
        onUploadComplete({ url: file_url, fileName: file.name, chunked: false });
        toast.success('File caricato con successo');
      } else {
        // Chunked upload
        toast.info('File grande rilevato. Inizio upload a chunked...');
        
        const { manifest, parts } = await chunkFile(file);
        const totalParts = parts.length + 1; // +1 for manifest
        let uploadedParts = 0;

        // Upload manifest
        const manifestBlob = new Blob([JSON.stringify(manifest)], { type: 'application/json' });
        const manifestFile = new File([manifestBlob], `${file.name}.manifest.json`);
        const { file_url: manifestUrl } = await base44.integrations.Core.UploadFile({ file: manifestFile });
        uploadedParts++;
        setProgress(Math.round((uploadedParts / totalParts) * 100));

        // Upload all parts
        const partUrls = [];
        for (const part of parts) {
          const partFile = new File([part.data], part.fileName);
          const { file_url: partUrl } = await base44.integrations.Core.UploadFile({ file: partFile });
          partUrls.push(partUrl);
          uploadedParts++;
          setProgress(Math.round((uploadedParts / totalParts) * 100));
        }

        setStatus('complete');
        setUploadedFile({ 
          manifestUrl, 
          partUrls, 
          fileName: file.name, 
          chunked: true,
          manifest 
        });
        
        onUploadComplete({ 
          manifestUrl, 
          partUrls, 
          fileName: file.name, 
          chunked: true 
        });
        
        toast.success(`File caricato in ${parts.length} parti`);
      }
    } catch (error) {
      console.error('Upload error:', error);
      setStatus('error');
      toast.error('Errore durante l\'upload');
    } finally {
      setIsUploading(false);
      e.target.value = '';
    }
  };

  const handleManifestUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const text = await file.text();
      const json = JSON.parse(text);
      setManifest(json);
      toast.success(`Manifest caricato: ${json.original.fileName}`);
    } catch (err) {
      toast.error(`Errore manifest: ${err.message}`);
    }
  };

  const handlePartFilesUpload = (e) => {
    const files = Array.from(e.target.files);
    setPartFiles(files);
    toast.info(`${files.length} parti selezionate`);
  };

  const uploadManualChunks = async () => {
    if (!manifest || partFiles.length === 0) return;

    const manifestParts = new Set(manifest.parts.map(p => p.fileName));
    const uploadedParts = new Set(partFiles.map(f => f.name));
    const missing = manifest.parts.filter(p => !uploadedParts.has(p.fileName)).map(p => p.fileName);

    if (missing.length > 0) {
      toast.error(`Parti mancanti: ${missing.slice(0, 3).join(', ')}${missing.length > 3 ? '...' : ''}`);
      return;
    }

    setIsUploading(true);
    setStatus('uploading');
    setProgress(0);

    try {
      // Sort part files to match manifest order
      const sortedPartFiles = manifest.parts.map(partInfo => 
        partFiles.find(f => f.name === partInfo.fileName)
      ).filter(f => f !== undefined);

      const totalParts = sortedPartFiles.length + 1;
      let uploadedCount = 0;

      // Upload manifest
      const manifestBlob = new Blob([JSON.stringify(manifest)], { type: 'application/json' });
      const manifestFile = new File([manifestBlob], `${manifest.original.fileName}.manifest.json`);
      const { file_url: manifestUrl } = await base44.integrations.Core.UploadFile({ file: manifestFile });
      uploadedCount++;
      setProgress(Math.round((uploadedCount / totalParts) * 100));

      // Upload parts
      const partUrls = [];
      for (let i = 0; i < sortedPartFiles.length; i++) {
        const { file_url } = await base44.integrations.Core.UploadFile({ file: sortedPartFiles[i] });
        partUrls.push(file_url);
        uploadedCount++;
        setProgress(Math.round((uploadedCount / totalParts) * 100));
      }

      setStatus('complete');
      setUploadedFile({
        manifestUrl,
        partUrls,
        fileName: manifest.original.fileName,
        chunked: true,
        manifest
      });

      onUploadComplete({
        manifestUrl,
        partUrls,
        fileName: manifest.original.fileName,
        chunked: true
      });

      toast.success('Upload completato!');
    } catch (err) {
      setStatus('error');
      toast.error(`Errore: ${err.message}`);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Mode Toggle */}
      <div className="flex gap-2">
        <Button
          type="button"
          variant={mode === 'single' ? 'default' : 'outline'}
          size="sm"
          onClick={() => {
            setMode('single');
            setManifest(null);
            setPartFiles([]);
            setStatus('idle');
          }}
          disabled={isUploading}
        >
          File Singolo
        </Button>
        <Button
          type="button"
          variant={mode === 'manual' ? 'default' : 'outline'}
          size="sm"
          onClick={() => {
            setMode('manual');
            setUploadedFile(null);
            setStatus('idle');
          }}
          disabled={isUploading}
        >
          Manifest + Parti
        </Button>
      </div>

      {mode === 'single' ? (
        <>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => document.getElementById('chunked-file-input').click()}
              disabled={disabled || isUploading}
            >
              <Upload className="w-4 h-4 mr-2" />
              {label}
            </Button>
            <input
              id="chunked-file-input"
              type="file"
              accept={accept}
              className="hidden"
              onChange={handleFileSelect}
            />
          </div>

          {isUploading && (
            <div className="space-y-2">
              <Progress value={progress} className="h-2" />
              <p className="text-xs text-[#7a7a7a]">Caricamento: {progress}%</p>
            </div>
          )}

          {status === 'complete' && uploadedFile && (
            <div className="flex items-center gap-2 text-xs text-green-600">
              <CheckCircle2 className="w-4 h-4" />
              <span>
                {uploadedFile.fileName} caricato
                {uploadedFile.chunked && ` (${uploadedFile.manifest.chunking.partCount} parti)`}
              </span>
            </div>
          )}
        </>
      ) : (
        <div className="space-y-3 border border-slate-200 rounded-lg p-4">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <FileJson className="w-4 h-4 text-purple-600" />
              <label className="text-sm font-medium">1. Carica Manifest (.json)</label>
            </div>
            <Input
              type="file"
              accept=".json"
              onChange={handleManifestUpload}
              disabled={isUploading}
            />
            {manifest && (
              <p className="text-xs text-green-600 mt-1">
                ✓ {manifest.original.fileName} ({manifest.chunking.partCount} parti richieste)
              </p>
            )}
          </div>

          <div>
            <div className="flex items-center gap-2 mb-2">
              <Package className="w-4 h-4 text-purple-600" />
              <label className="text-sm font-medium">2. Carica Parti (.part00000, .part00001, ...)</label>
            </div>
            <Input
              type="file"
              multiple
              onChange={handlePartFilesUpload}
              disabled={isUploading || !manifest}
            />
            {partFiles.length > 0 && (
              <p className="text-xs text-green-600 mt-1">
                ✓ {partFiles.length} parti selezionate
              </p>
            )}
          </div>

          {manifest && partFiles.length > 0 && (
            <Button
              type="button"
              onClick={uploadManualChunks}
              disabled={isUploading}
              className="w-full"
            >
              <Upload className="w-4 h-4 mr-2" />
              Carica Tutti i File
            </Button>
          )}

          {isUploading && (
            <div className="space-y-2">
              <Progress value={progress} className="h-2" />
              <p className="text-xs text-[#7a7a7a]">Caricamento: {progress}%</p>
            </div>
          )}

          {status === 'complete' && uploadedFile && (
            <div className="flex items-center gap-2 text-xs text-green-600">
              <CheckCircle2 className="w-4 h-4" />
              <span>{uploadedFile.fileName} caricato ({uploadedFile.manifest.chunking.partCount} parti)</span>
            </div>
          )}
        </div>
      )}

      {status === 'error' && (
        <div className="flex items-center gap-2 text-xs text-red-600">
          <AlertCircle className="w-4 h-4" />
          <span>Errore durante l'upload</span>
        </div>
      )}
    </div>
  );
}

export { reconstructFile };